import React, { useRef } from 'react';
import { HighlightRect } from '../../../sdk/@types/Acertei';

type Mode = 'draw' | 'move' | 'resize';

const HANDLE_PX = 10;

interface UseHighlightDrawOptions {
  existingRect: HighlightRect | null;
  selectedPage: number;
  pageRefs: React.MutableRefObject<(HTMLDivElement | null)[]>;
  onChange: (rect: HighlightRect, page: number) => void;
}

export default function useHighlightDraw({ existingRect, selectedPage, pageRefs, onChange }: UseHighlightDrawOptions) {
  const dragging = useRef(false);
  const mode = useRef<Mode>('draw');
  const dragStart = useRef<{ clientX: number; clientY: number } | null>(null);
  const rectAtStart = useRef<HighlightRect | null>(null);
  const drawStartPct = useRef<{ x: number; y: number } | null>(null);
  const activePage = useRef(0);

  const handleMouseDown = (e: React.MouseEvent, pageIndex: number) => {
    e.preventDefault();
    const el = pageRefs.current[pageIndex];
    if (!el) return;

    const elRect = el.getBoundingClientRect();
    const mx = e.clientX - elRect.left;
    const my = e.clientY - elRect.top;

    dragging.current = true;
    dragStart.current = { clientX: e.clientX, clientY: e.clientY };
    activePage.current = pageIndex;
    rectAtStart.current = existingRect;

    if (existingRect && pageIndex + 1 === selectedPage) {
      const ex = (existingRect.x / 100) * el.offsetWidth;
      const ey = (existingRect.y / 100) * el.offsetHeight;
      const ew = (existingRect.w / 100) * el.offsetWidth;
      const eh = (existingRect.h / 100) * el.offsetHeight;

      if (Math.abs(mx - (ex + ew)) <= HANDLE_PX && Math.abs(my - (ey + eh)) <= HANDLE_PX) {
        mode.current = 'resize';
        return;
      }
      if (mx >= ex && mx <= ex + ew && my >= ey && my <= ey + eh) {
        mode.current = 'move';
        return;
      }
    }

    mode.current = 'draw';
    drawStartPct.current = {
      x: (mx / el.offsetWidth) * 100,
      y: (my / el.offsetHeight) * 100,
    };
  };

  const handleMouseMove = (e: React.MouseEvent, pageIndex: number) => {
    if (!dragging.current || !dragStart.current || pageIndex !== activePage.current) return;
    const el = pageRefs.current[pageIndex];
    if (!el) return;

    const elRect = el.getBoundingClientRect();

    if (mode.current === 'draw' && drawStartPct.current) {
      const curX = ((e.clientX - elRect.left) / el.offsetWidth) * 100;
      const curY = ((e.clientY - elRect.top) / el.offsetHeight) * 100;
      const s = drawStartPct.current;
      onChange(
        {
          x: Math.min(s.x, curX),
          y: Math.min(s.y, curY),
          w: Math.abs(curX - s.x),
          h: Math.abs(curY - s.y),
        },
        pageIndex + 1
      );
    } else if (mode.current === 'move' && rectAtStart.current) {
      const dx = ((e.clientX - dragStart.current.clientX) / el.offsetWidth) * 100;
      const dy = ((e.clientY - dragStart.current.clientY) / el.offsetHeight) * 100;
      const b = rectAtStart.current;
      onChange(
        {
          x: Math.max(0, Math.min(100 - b.w, b.x + dx)),
          y: Math.max(0, Math.min(100 - b.h, b.y + dy)),
          w: b.w,
          h: b.h,
        },
        pageIndex + 1
      );
    } else if (mode.current === 'resize' && rectAtStart.current) {
      const dx = ((e.clientX - dragStart.current.clientX) / el.offsetWidth) * 100;
      const dy = ((e.clientY - dragStart.current.clientY) / el.offsetHeight) * 100;
      const b = rectAtStart.current;
      onChange(
        {
          x: b.x,
          y: b.y,
          w: Math.max(1, b.w + dx),
          h: Math.max(1, b.h + dy),
        },
        pageIndex + 1
      );
    }
  };

  const handleMouseUp = () => {
    dragging.current = false;
    dragStart.current = null;
    drawStartPct.current = null;
  };

  return { handleMouseDown, handleMouseMove, handleMouseUp };
}
